import { db } from './config';
import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where,
} from 'firebase/firestore';

// ── Core interfaces ──────────────────────────────────────────

export interface InventoryItem {
  id?: string;
  name: string;
  category: string;
  quantity: number;
  minimumQuantity: number;
  unit: string; // 'ml', 'unidade', 'caixa'
  unitCost?: number;
  expiryDate?: string; // ISO date
  linkedProcedureIds?: string[];
  lastRestockDate?: string;
}

export interface ProcedureMaterialLink {
  id?: string;
  procedureId: string;
  materialId: string;
  quantityPerUse: number;
  createdAt: string;
}

export interface InventoryMovement {
  id?: string;
  itemId: string;
  type: 'IN' | 'OUT' | 'ADJUSTMENT';
  quantity: number;
  reason: string;
  balanceAfter: number;
  createdAt: string;
  createdBy?: string;
}

export interface StockAlert {
  itemId: string;
  itemName: string;
  currentQuantity: number;
  minimumQuantity: number;
  unit: string;
}

export interface ExpiryAlert {
  itemId: string;
  itemName: string;
  expiryDate: string;
  daysUntilExpiry: number;
  quantity: number;
}

export interface CostEntry {
  id?: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  patientId?: string;
  insurerId?: string;
  clinicId: string;
  createdAt: string;
}

// ── Service ──────────────────────────────────────────────────

export const InventoryService = {
  // ── CRUD (existing) ────────────────────────────────────────

  async getItems(clinicId: string): Promise<InventoryItem[]> {
    const colRef = collection(db, 'clinics', clinicId, 'inventory');
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem));
  },

  async addItem(clinicId: string, item: Omit<InventoryItem, 'id'>): Promise<string> {
    const colRef = collection(db, 'clinics', clinicId, 'inventory');
    const docRef = await addDoc(colRef, item);
    return docRef.id;
  },

  async updateItem(clinicId: string, id: string, data: Partial<InventoryItem>): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'inventory', id);
    await updateDoc(docRef, data);
  },

  async deleteItem(clinicId: string, id: string): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'inventory', id);
    await deleteDoc(docRef);
  },

  // ── T239: Procedure-Inventory Linkage ──────────────────────

  async linkProcedureToMaterial(
    clinicId: string,
    procedureId: string,
    materialId: string,
    quantity: number,
  ): Promise<string> {
    const colRef = collection(db, 'clinics', clinicId, 'procedureMaterialLinks');
    const link: Omit<ProcedureMaterialLink, 'id'> = {
      procedureId,
      materialId,
      quantityPerUse: quantity,
      createdAt: new Date().toISOString(),
    };
    const docRef = await addDoc(colRef, link);

    // Also update the item's linkedProcedureIds array
    const item = (await getDocs(
      query(collection(db, 'clinics', clinicId, 'inventory'), where('__name__', '==', materialId)),
    )).docs[0];
    if (item) {
      const existing = (item.data().linkedProcedureIds as string[]) ?? [];
      if (!existing.includes(procedureId)) {
        await updateDoc(doc(db, 'clinics', clinicId, 'inventory', materialId), {
          linkedProcedureIds: [...existing, procedureId],
        });
      }
    }

    return docRef.id;
  },

  async getLinksForProcedure(
    clinicId: string,
    procedureId: string,
  ): Promise<ProcedureMaterialLink[]> {
    const colRef = collection(db, 'clinics', clinicId, 'procedureMaterialLinks');
    const q = query(colRef, where('procedureId', '==', procedureId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ProcedureMaterialLink));
  },

  async autoDeductOnProcedure(clinicId: string, procedureId: string): Promise<void> {
    const links = await this.getLinksForProcedure(clinicId, procedureId);
    const items = await this.getItems(clinicId);
    const itemMap = new Map(items.map(i => [i.id!, i]));

    for (const link of links) {
      const item = itemMap.get(link.materialId);
      if (!item) continue;

      const newQuantity = Math.max(0, item.quantity - link.quantityPerUse);

      // Update the stock
      await this.updateItem(clinicId, link.materialId, { quantity: newQuantity });

      // Record the movement
      await this.addMovement(clinicId, {
        itemId: link.materialId,
        type: 'OUT',
        quantity: link.quantityPerUse,
        reason: `Procedimento automatico: ${procedureId}`,
        balanceAfter: newQuantity,
        createdAt: new Date().toISOString(),
      });
    }
  },

  // ── T239 + T241: Movement tracking ─────────────────────────

  async addMovement(
    clinicId: string,
    movement: Omit<InventoryMovement, 'id'>,
  ): Promise<string> {
    const colRef = collection(db, 'clinics', clinicId, 'inventoryMovements');
    const docRef = await addDoc(colRef, movement);
    return docRef.id;
  },

  async getMovements(clinicId: string, itemId?: string): Promise<InventoryMovement[]> {
    const colRef = collection(db, 'clinics', clinicId, 'inventoryMovements');
    const q = itemId
      ? query(colRef, where('itemId', '==', itemId))
      : colRef;
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() } as InventoryMovement))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  /** T241: Periodic inventory — register a counted quantity and create adjustment */
  async registerPeriodicCount(
    clinicId: string,
    itemId: string,
    countedQuantity: number,
    countedBy?: string,
  ): Promise<void> {
    const items = await this.getItems(clinicId);
    const item = items.find(i => i.id === itemId);
    if (!item) throw new Error(`Item ${itemId} not found`);

    const diff = countedQuantity - item.quantity;
    if (diff === 0) return; // no adjustment needed

    // Update to the counted quantity
    await this.updateItem(clinicId, itemId, { quantity: countedQuantity });

    // Record adjustment movement
    await this.addMovement(clinicId, {
      itemId,
      type: 'ADJUSTMENT',
      quantity: diff,
      reason: `Inventario periodico${countedBy ? ` por ${countedBy}` : ''}`,
      balanceAfter: countedQuantity,
      createdAt: new Date().toISOString(),
      createdBy: countedBy,
    });
  },

  // ── T240: Stock & Expiry Alerts ────────────────────────────

  async getStockAlerts(clinicId: string): Promise<StockAlert[]> {
    const items = await this.getItems(clinicId);
    return items
      .filter(item => item.quantity <= item.minimumQuantity)
      .map(item => ({
        itemId: item.id!,
        itemName: item.name,
        currentQuantity: item.quantity,
        minimumQuantity: item.minimumQuantity,
        unit: item.unit,
      }));
  },

  async getExpiryAlerts(clinicId: string): Promise<ExpiryAlert[]> {
    const items = await this.getItems(clinicId);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return items
      .filter(item => {
        if (!item.expiryDate) return false;
        const expiry = new Date(item.expiryDate);
        return expiry <= thirtyDaysFromNow;
      })
      .map(item => {
        const expiry = new Date(item.expiryDate!);
        const diffMs = expiry.getTime() - now.getTime();
        const daysUntilExpiry = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return {
          itemId: item.id!,
          itemName: item.name,
          expiryDate: item.expiryDate!,
          daysUntilExpiry,
          quantity: item.quantity,
        };
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  },

  // ── T242: Cost Attribution ─────────────────────────────────

  async attributeCost(
    clinicId: string,
    itemId: string,
    patientId?: string,
    insurerId?: string,
  ): Promise<string> {
    const items = await this.getItems(clinicId);
    const item = items.find(i => i.id === itemId);
    if (!item) throw new Error(`Item ${itemId} not found`);

    const unitCost = item.unitCost ?? 0;
    const costEntry: Omit<CostEntry, 'id'> = {
      itemId,
      itemName: item.name,
      quantity: 1,
      unitCost,
      totalCost: unitCost,
      patientId,
      insurerId,
      clinicId,
      createdAt: new Date().toISOString(),
    };

    const colRef = collection(db, 'clinics', clinicId, 'costEntries');
    const docRef = await addDoc(colRef, costEntry);
    return docRef.id;
  },

  async getCostEntries(
    clinicId: string,
    filters?: { patientId?: string; insurerId?: string },
  ): Promise<CostEntry[]> {
    const colRef = collection(db, 'clinics', clinicId, 'costEntries');
    let q;
    if (filters?.patientId) {
      q = query(colRef, where('patientId', '==', filters.patientId));
    } else if (filters?.insurerId) {
      q = query(colRef, where('insurerId', '==', filters.insurerId));
    } else {
      q = colRef;
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CostEntry));
  },

  // Legacy compat
  async deductStock(clinicId: string, procedureId: string): Promise<void> {
    await this.autoDeductOnProcedure(clinicId, procedureId);
  },
};
